import React, { useEffect, useRef, useState } from "react";

/**
 * Themed animated decoration overlay for premium invitations.
 * - Pure SVG + CSS animations, no external images.
 * - Parallax on scroll (elements move slower than main content).
 * - Section-triggered fade/scale/slide reveal on IntersectionObserver.
 *
 * Rendered ABOVE the phone-card background image but BELOW the content
 * (z-index between them). Pointer-events:none so it never blocks input.
 */
export default function ThemeDecoration({ theme }) {
  const rootRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let raf = 0;
    let latest = 0;
    const onScroll = () => {
      latest = window.scrollY || window.pageYOffset || 0;
      if (raf) return;
      raf = requestAnimationFrame(() => { setScrollY(latest); raf = 0; });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);

  // Hide when tab not visible for perf on mobile.
  useEffect(() => {
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const pal = theme.palette;
  const acc = pal.accent;
  const acc2 = pal.accent2 || pal.accent;

  // Parallax offset factors — different for each layer
  const px = (factor) => `translate3d(0, ${(scrollY * factor) * -1}px, 0)`;

  // Common style for each floating decorative layer
  const layerStyle = (factor, opacity = 0.85) => ({
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    willChange: "transform, opacity",
    transform: px(factor),
    opacity: visible ? opacity : 0,
    transition: "opacity 0.4s ease",
  });

  return (
    <div ref={rootRef} className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="theme-decoration" aria-hidden="true">
      <style>{CSS_KEYFRAMES}</style>
      {theme.id === "editorial-bw" && <EditorialBW acc={acc} px={px} layerStyle={layerStyle} />}
      {theme.id === "ar-rum-modern" && <ArRumModern acc={acc} acc2={acc2} px={px} layerStyle={layerStyle} />}
      {theme.id === "vintage-floral" && <VintageFloral acc={acc} acc2={acc2} px={px} layerStyle={layerStyle} />}
      {theme.id === "dark-luxury" && <DarkLuxury acc={acc} acc2={acc2} px={px} layerStyle={layerStyle} />}
      {theme.id === "pastel-watercolor" && <PastelWatercolor acc={acc} acc2={acc2} px={px} layerStyle={layerStyle} />}
      {theme.id === "botanical-green" && <BotanicalGreen acc={acc} acc2={acc2} px={px} layerStyle={layerStyle} />}
      {theme.id === "minimal-contemporary" && <MinimalContemporary acc={acc} px={px} layerStyle={layerStyle} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Global keyframes (once). Kept small — used across variants.                */
/* -------------------------------------------------------------------------- */
const CSS_KEYFRAMES = `
@keyframes wl-float-slow  { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-14px) rotate(3deg)} }
@keyframes wl-float-slow-r{ 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-10px) rotate(-4deg)} }
@keyframes wl-drift-1 { 0%{transform:translate3d(-6vw,110vh,0) rotate(0deg);opacity:0} 10%{opacity:1} 90%{opacity:1} 100%{transform:translate3d(6vw,-10vh,0) rotate(360deg);opacity:0} }
@keyframes wl-drift-2 { 0%{transform:translate3d(8vw,110vh,0) rotate(0deg);opacity:0} 15%{opacity:.85} 85%{opacity:.85} 100%{transform:translate3d(-4vw,-10vh,0) rotate(-320deg);opacity:0} }
@keyframes wl-drift-3 { 0%{transform:translate3d(-2vw,110vh,0) rotate(20deg);opacity:0} 20%{opacity:.7} 80%{opacity:.7} 100%{transform:translate3d(10vw,-10vh,0) rotate(400deg);opacity:0} }
@keyframes wl-twinkle { 0%,100%{opacity:.15;transform:scale(.8)} 50%{opacity:1;transform:scale(1.2)} }
@keyframes wl-shine   { 0%,100%{opacity:.25} 50%{opacity:.9} }
@keyframes wl-sway    { 0%,100%{transform:rotate(-3deg)} 50%{transform:rotate(3deg)} }
@keyframes wl-sway-r  { 0%,100%{transform:rotate(3deg)} 50%{transform:rotate(-3deg)} }
@keyframes wl-blob    { 0%,100%{transform:scale(1) translate(0,0)} 50%{transform:scale(1.15) translate(10px,-6px)} }
@keyframes wl-orbit   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
@keyframes wl-slide-h { 0%,100%{transform:translateX(-10px)} 50%{transform:translateX(10px)} }
`;

/* -------------------------------------------------------------------------- */
/* 1) EDITORIAL BW — geometric lines + dots, monochrome                        */
/* -------------------------------------------------------------------------- */
function EditorialBW({ acc, layerStyle }) {
  const dots = Array.from({ length: 8 });
  return (
    <>
      {/* Slow long vertical lines, parallax factor 0.05 */}
      <div style={layerStyle(0.06, 0.35)}>
        <svg width="100%" height="100%" viewBox="0 0 430 900" preserveAspectRatio="none">
          <line x1="24" y1="0" x2="24" y2="900" stroke={acc} strokeWidth="0.4" strokeDasharray="1 6" />
          <line x1="406" y1="0" x2="406" y2="900" stroke={acc} strokeWidth="0.4" strokeDasharray="1 6" />
        </svg>
      </div>
      {/* Floating dots, parallax factor 0.15 */}
      <div style={layerStyle(0.15, 0.7)}>
        {dots.map((_, i) => (
          <span key={i}
            style={{
              position: "absolute",
              width: 3, height: 3, borderRadius: 999,
              background: acc,
              top: `${(i * 13 + 5) % 95}%`,
              left: `${(i * 27 + 8) % 95}%`,
              animation: `wl-twinkle ${3 + (i % 4)}s ease-in-out ${i * 0.4}s infinite`,
            }}
          />
        ))}
      </div>
      {/* Corner geometric marks */}
      <div style={layerStyle(0.02, 0.6)}>
        <svg width="60" height="60" viewBox="0 0 60 60" style={{ position: "absolute", top: 24, right: 24 }}>
          <path d="M0 4 L20 4 L20 0" stroke={acc} strokeWidth="0.6" fill="none" />
          <path d="M4 0 L4 20 L0 20" stroke={acc} strokeWidth="0.6" fill="none" />
        </svg>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 2) AR-RUM MODERN — Arabic calligraphy motif + twinkling stars              */
/* -------------------------------------------------------------------------- */
function ArRumModern({ acc, acc2, layerStyle }) {
  const stars = Array.from({ length: 22 });
  return (
    <>
      {/* Arabic calligraphy watermark, low opacity */}
      <div style={layerStyle(0.04, 0.06)}>
        <svg width="100%" height="100%" viewBox="0 0 430 900" preserveAspectRatio="xMidYMid meet">
          <text x="215" y="240" textAnchor="middle" fontFamily="'Amiri', 'Scheherazade', serif" fontSize="120" fill={acc2}>
            ﷽
          </text>
          <text x="215" y="700" textAnchor="middle" fontFamily="'Amiri', 'Scheherazade', serif" fontSize="90" fill={acc2}>
            الحمد لله
          </text>
        </svg>
      </div>
      {/* Twinkling stars */}
      <div style={layerStyle(0.12, 0.9)}>
        {stars.map((_, i) => (
          <svg key={i}
            width={6 + (i % 3) * 3} height={6 + (i % 3) * 3} viewBox="0 0 20 20"
            style={{
              position: "absolute",
              top: `${(i * 7 + 3) % 98}%`,
              left: `${(i * 19 + 11) % 96}%`,
              animation: `wl-twinkle ${2.5 + (i % 5) * 0.7}s ease-in-out ${i * 0.15}s infinite`,
              filter: `drop-shadow(0 0 6px ${acc2}66)`,
            }}
          >
            <path d="M10 0 L12 8 L20 10 L12 12 L10 20 L8 12 L0 10 L8 8 Z" fill={acc2} />
          </svg>
        ))}
      </div>
      {/* Arch ornament (top) */}
      <div style={layerStyle(0.08, 0.5)}>
        <svg width="120" height="60" viewBox="0 0 120 60" style={{ position: "absolute", top: 20, left: "50%", transform: "translateX(-50%)" }}>
          <path d="M0 55 Q60 -10 120 55" fill="none" stroke={acc} strokeWidth="1" />
          <circle cx="60" cy="55" r="2" fill={acc} />
        </svg>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 3) VINTAGE FLORAL — rose petals + leaves drifting                          */
/* -------------------------------------------------------------------------- */
function VintageFloral({ acc, acc2, layerStyle }) {
  const petals = Array.from({ length: 14 });
  return (
    <>
      {/* Slow floating background — pinkish blurred blobs */}
      <div style={layerStyle(0.03, 0.35)}>
        <div style={{
          position: "absolute", top: "10%", left: "-15%", width: 260, height: 260,
          borderRadius: "50%", background: `radial-gradient(circle, ${acc}55 0%, transparent 70%)`,
          animation: "wl-blob 8s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute", bottom: "8%", right: "-18%", width: 300, height: 300,
          borderRadius: "50%", background: `radial-gradient(circle, ${acc2}55 0%, transparent 70%)`,
          animation: "wl-blob 10s ease-in-out infinite reverse",
        }} />
      </div>
      {/* Falling petals */}
      <div style={layerStyle(0.18, 1)}>
        {petals.map((_, i) => {
          const size = 10 + (i % 4) * 4;
          const dur = 14 + (i % 6) * 2;
          const delay = (i * 0.9) % dur;
          const anim = i % 3 === 0 ? "wl-drift-1" : i % 3 === 1 ? "wl-drift-2" : "wl-drift-3";
          return (
            <svg key={i}
              width={size} height={size} viewBox="0 0 20 20"
              style={{
                position: "absolute",
                top: 0,
                left: `${(i * 13 + 4) % 92}%`,
                animation: `${anim} ${dur}s linear ${-delay}s infinite`,
              }}
            >
              <path d="M10 2 Q4 6 6 12 Q10 18 14 12 Q16 6 10 2 Z" fill={acc} opacity="0.85" />
              <path d="M10 4 Q8 10 10 14 Q12 10 10 4 Z" fill={acc2} opacity="0.5" />
            </svg>
          );
        })}
      </div>
      {/* Drifting leaves */}
      <div style={layerStyle(0.12, 0.8)}>
        {petals.slice(0, 6).map((_, i) => (
          <svg key={`leaf-${i}`} width="16" height="24" viewBox="0 0 16 24"
            style={{
              position: "absolute",
              top: 0,
              left: `${(i * 21 + 12) % 90}%`,
              animation: `wl-drift-2 ${18 + i}s linear ${-i * 3}s infinite`,
              transformOrigin: "center",
            }}
          >
            <path d="M8 0 Q14 8 10 22 Q6 18 4 12 Q2 6 8 0 Z" fill={acc} opacity="0.55" />
            <line x1="8" y1="2" x2="8" y2="22" stroke={acc2} strokeWidth="0.4" opacity="0.7" />
          </svg>
        ))}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 4) DARK LUXURY — art deco frames + gold sparkles                            */
/* -------------------------------------------------------------------------- */
function DarkLuxury({ acc, acc2, layerStyle }) {
  const sparkles = Array.from({ length: 30 });
  return (
    <>
      {/* Art deco border pattern (top/bottom) */}
      <div style={layerStyle(0.05, 0.35)}>
        <svg width="100%" height="60" viewBox="0 0 430 60" preserveAspectRatio="none" style={{ position: "absolute", top: 8, left: 0 }}>
          <g stroke={acc} strokeWidth="0.7" fill="none">
            <line x1="20" y1="30" x2="180" y2="30" />
            <line x1="250" y1="30" x2="410" y2="30" />
            <path d="M195 30 L215 12 L235 30 L215 48 Z" />
            <path d="M205 30 L215 20 L225 30 L215 40 Z" fill={acc} opacity="0.65" />
          </g>
        </svg>
        <svg width="100%" height="60" viewBox="0 0 430 60" preserveAspectRatio="none" style={{ position: "absolute", bottom: 8, left: 0 }}>
          <g stroke={acc} strokeWidth="0.7" fill="none">
            <line x1="20" y1="30" x2="180" y2="30" />
            <line x1="250" y1="30" x2="410" y2="30" />
            <path d="M195 30 L215 12 L235 30 L215 48 Z" />
          </g>
        </svg>
      </div>
      {/* Sparkle particles */}
      <div style={layerStyle(0.14, 1)}>
        {sparkles.map((_, i) => {
          const s = 2 + (i % 3);
          const dur = 2 + (i % 5) * 0.6;
          return (
            <span key={i}
              style={{
                position: "absolute",
                width: s, height: s, borderRadius: 999,
                top: `${(i * 5 + 3) % 97}%`,
                left: `${(i * 23 + 7) % 96}%`,
                background: acc2,
                boxShadow: `0 0 ${4 + s * 2}px ${acc2}`,
                animation: `wl-twinkle ${dur}s ease-in-out ${i * 0.12}s infinite`,
              }}
            />
          );
        })}
      </div>
      {/* Rotating diamond mark (parallax slow) */}
      <div style={layerStyle(0.08, 0.28)}>
        <div style={{
          position: "absolute", top: "38%", right: "-30px", width: 80, height: 80,
          animation: "wl-orbit 40s linear infinite",
        }}>
          <svg width="80" height="80" viewBox="0 0 80 80">
            <path d="M40 4 L76 40 L40 76 L4 40 Z" stroke={acc} strokeWidth="0.5" fill="none" />
            <path d="M40 20 L60 40 L40 60 L20 40 Z" stroke={acc2} strokeWidth="0.5" fill="none" />
          </svg>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 5) PASTEL WATERCOLOR — pastel petals + watercolor bubbles                   */
/* -------------------------------------------------------------------------- */
function PastelWatercolor({ acc, acc2, layerStyle }) {
  const bubbles = Array.from({ length: 8 });
  const petals = Array.from({ length: 10 });
  return (
    <>
      {/* Watercolor blobs */}
      <div style={layerStyle(0.04, 0.6)}>
        <div style={{
          position: "absolute", top: "12%", left: "-20%", width: 340, height: 340,
          borderRadius: "50%", filter: "blur(30px)",
          background: `radial-gradient(circle, ${acc}77 0%, transparent 70%)`,
          animation: "wl-blob 12s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute", top: "55%", right: "-25%", width: 380, height: 380,
          borderRadius: "50%", filter: "blur(36px)",
          background: `radial-gradient(circle, ${acc2}77 0%, transparent 70%)`,
          animation: "wl-blob 14s ease-in-out infinite reverse",
        }} />
      </div>
      {/* Floating bubbles */}
      <div style={layerStyle(0.15, 0.9)}>
        {bubbles.map((_, i) => (
          <div key={i}
            style={{
              position: "absolute",
              width: 16 + (i % 4) * 6, height: 16 + (i % 4) * 6, borderRadius: 999,
              top: 0, left: `${(i * 17 + 8) % 90}%`,
              background: `radial-gradient(circle at 30% 30%, #ffffffcc 0%, ${i % 2 ? acc : acc2}44 60%, transparent 100%)`,
              animation: `wl-drift-2 ${16 + i * 2}s linear ${-i * 2}s infinite`,
            }}
          />
        ))}
      </div>
      {/* Soft petals */}
      <div style={layerStyle(0.11, 1)}>
        {petals.map((_, i) => (
          <svg key={i} width="14" height="14" viewBox="0 0 20 20"
            style={{
              position: "absolute", top: 0, left: `${(i * 14 + 7) % 92}%`,
              animation: `wl-drift-1 ${18 + i}s linear ${-i * 1.5}s infinite`,
            }}
          >
            <path d="M10 2 Q4 6 6 12 Q10 18 14 12 Q16 6 10 2 Z" fill={i % 2 ? acc : acc2} opacity="0.7" />
          </svg>
        ))}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 6) BOTANICAL GREEN — tropical leaves swaying                                */
/* -------------------------------------------------------------------------- */
function BotanicalGreen({ acc, acc2, layerStyle }) {
  const leaves = Array.from({ length: 8 });
  return (
    <>
      {/* Large leaves at corners */}
      <div style={layerStyle(0.03, 0.4)}>
        <svg width="180" height="220" viewBox="0 0 180 220" style={{ position: "absolute", top: -20, left: -40, transformOrigin: "50% 20%", animation: "wl-sway 6s ease-in-out infinite" }}>
          <g fill={acc} opacity="0.6">
            <path d="M60 10 Q120 30 130 120 Q90 110 60 60 Q30 30 60 10 Z" />
            <path d="M60 10 Q40 60 60 110" stroke={acc2} strokeWidth="1" fill="none" />
          </g>
        </svg>
        <svg width="180" height="220" viewBox="0 0 180 220" style={{ position: "absolute", bottom: -20, right: -40, transformOrigin: "50% 80%", animation: "wl-sway-r 7s ease-in-out infinite", transform: "scaleX(-1)" }}>
          <g fill={acc} opacity="0.5">
            <path d="M60 10 Q120 30 130 120 Q90 110 60 60 Q30 30 60 10 Z" />
            <path d="M60 10 Q40 60 60 110" stroke={acc2} strokeWidth="1" fill="none" />
          </g>
        </svg>
      </div>
      {/* Floating small leaves */}
      <div style={layerStyle(0.16, 0.85)}>
        {leaves.map((_, i) => (
          <svg key={i} width="24" height="34" viewBox="0 0 24 34"
            style={{
              position: "absolute",
              top: 0,
              left: `${(i * 15 + 8) % 90}%`,
              animation: `wl-drift-3 ${20 + i * 2}s linear ${-i * 3}s infinite`,
              transformOrigin: "50% 30%",
            }}
          >
            <path d="M12 0 Q22 12 15 32 Q9 26 6 18 Q3 8 12 0 Z" fill={acc} opacity="0.75" />
            <line x1="12" y1="2" x2="12" y2="32" stroke={acc2} strokeWidth="0.5" />
          </svg>
        ))}
      </div>
      {/* Twig sway (side) */}
      <div style={layerStyle(0.06, 0.5)}>
        <svg width="60" height="180" viewBox="0 0 60 180" style={{ position: "absolute", top: "30%", right: 4, animation: "wl-sway 5s ease-in-out infinite", transformOrigin: "top center" }}>
          <path d="M30 0 Q28 60 30 120 Q32 150 30 180" stroke={acc} strokeWidth="1.5" fill="none" />
          <path d="M30 40 L18 30" stroke={acc} strokeWidth="1" />
          <path d="M30 70 L44 60" stroke={acc} strokeWidth="1" />
          <path d="M30 100 L18 90" stroke={acc} strokeWidth="1" />
          <ellipse cx="16" cy="30" rx="6" ry="3" fill={acc} opacity="0.7" transform="rotate(-30 16 30)" />
          <ellipse cx="46" cy="60" rx="6" ry="3" fill={acc} opacity="0.7" transform="rotate(30 46 60)" />
          <ellipse cx="16" cy="90" rx="6" ry="3" fill={acc} opacity="0.7" transform="rotate(-30 16 90)" />
        </svg>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 7) MINIMAL CONTEMPORARY — thin geometric lines & shapes drifting            */
/* -------------------------------------------------------------------------- */
function MinimalContemporary({ acc, layerStyle }) {
  return (
    <>
      {/* Slow drifting thin square outline */}
      <div style={layerStyle(0.05, 0.45)}>
        <div style={{
          position: "absolute", top: "18%", left: "-24px", width: 90, height: 90,
          border: `1px solid ${acc}`, transform: "rotate(12deg)",
          animation: "wl-sway 8s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute", top: "62%", right: "-30px", width: 120, height: 120,
          border: `1px solid ${acc}`, borderRadius: "50%",
          animation: "wl-blob 10s ease-in-out infinite",
        }} />
      </div>
      {/* Horizontal thin lines swaying */}
      <div style={layerStyle(0.09, 0.5)}>
        <div style={{
          position: "absolute", top: "36%", left: 0, height: 1, width: "120%",
          background: `linear-gradient(90deg, transparent, ${acc}55, transparent)`,
          animation: "wl-slide-h 12s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute", top: "78%", left: 0, height: 1, width: "120%",
          background: `linear-gradient(90deg, transparent, ${acc}66, transparent)`,
          animation: "wl-slide-h 14s ease-in-out infinite reverse",
        }} />
      </div>
      {/* Tiny orbiting triangle */}
      <div style={layerStyle(0.14, 0.7)}>
        <div style={{ position: "absolute", top: "30%", left: "50%", width: 0, height: 0, animation: "wl-orbit 30s linear infinite", transformOrigin: "0 60px" }}>
          <div style={{ width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderBottom: `8px solid ${acc}` }} />
        </div>
      </div>
    </>
  );
}
